/**
 * Tenant Details Step (Step 1)
 *
 * Collects basic tenant information: name, slug, and optional description.
 * Slug is auto-generated from name but remains editable.
 */

import type { ReactElement, ChangeEvent } from "react";
import { useCallback } from "react";

export interface TenantDetailsData {
  readonly name: string;
  readonly slug: string;
  readonly description: string;
}

interface TenantDetailsStepProps {
  readonly data: TenantDetailsData;
  readonly errors: Record<string, string>;
  readonly onChange: (data: TenantDetailsData) => void;
}

/**
 * Generate a URL-friendly slug from a tenant name.
 */
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}

export function TenantDetailsStep({
  data,
  errors,
  onChange,
}: TenantDetailsStepProps): ReactElement {
  const handleNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const name = e.target.value;
      // Auto-generate slug from name only if slug hasn't been manually edited
      const autoSlug = data.slug === generateSlug(data.name) || data.slug === "";
      onChange({
        ...data,
        name,
        slug: autoSlug ? generateSlug(name) : data.slug,
      });
    },
    [data, onChange]
  );

  const handleSlugChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-");
      onChange({ ...data, slug });
    },
    [data, onChange]
  );

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>): void => {
      onChange({ ...data, description: e.target.value });
    },
    [data, onChange]
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Tenant Details</h2>
        <p className="mt-1 text-sm text-foreground-muted">
          Provide basic information about the new tenant organization.
        </p>
      </div>

      <div className="card space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="tenant-name" className="block text-sm font-medium mb-1">
            Tenant Name <span className="text-error">*</span>
          </label>
          <input
            id="tenant-name"
            type="text"
            value={data.name}
            onChange={handleNameChange}
            placeholder="e.g., Acme Insurance"
            className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
              errors["name"]
                ? "border-error focus:border-error focus:ring-error"
                : "border-border focus:border-primary focus:ring-primary"
            }`}
          />
          {errors["name"] && <p className="mt-1 text-sm text-error">{errors["name"]}</p>}
        </div>

        {/* Slug */}
        <div>
          <label htmlFor="tenant-slug" className="block text-sm font-medium mb-1">
            Slug <span className="text-error">*</span>
          </label>
          <input
            id="tenant-slug"
            type="text"
            value={data.slug}
            onChange={handleSlugChange}
            placeholder="e.g., acme-insurance"
            className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
              errors["slug"]
                ? "border-error focus:border-error focus:ring-error"
                : "border-border focus:border-primary focus:ring-primary"
            }`}
          />
          <p className="mt-1 text-xs text-foreground-muted">
            Used in URLs and API calls. Only lowercase letters, numbers, and hyphens.
          </p>
          {errors["slug"] && <p className="mt-1 text-sm text-error">{errors["slug"]}</p>}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="tenant-description" className="block text-sm font-medium mb-1">
            Description
          </label>
          <textarea
            id="tenant-description"
            value={data.description}
            onChange={handleDescriptionChange}
            placeholder="Brief description of the tenant organization"
            rows={3}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Validate the tenant details step data.
 * Returns an errors record; empty means valid.
 */
export function validateTenantDetails(data: TenantDetailsData): Record<string, string> {
  const errors: Record<string, string> = {};

  const trimmedName = data.name.trim();
  if (!trimmedName) {
    errors["name"] = "Name is required";
  } else if (trimmedName.length < 2) {
    errors["name"] = "Name must be at least 2 characters";
  } else if (trimmedName.length > 100) {
    errors["name"] = "Name must be less than 100 characters";
  }

  const trimmedSlug = data.slug.trim();
  if (!trimmedSlug) {
    errors["slug"] = "Slug is required";
  } else if (!/^[a-z0-9-]+$/.test(trimmedSlug)) {
    errors["slug"] = "Slug can only contain lowercase letters, numbers, and hyphens";
  } else if (trimmedSlug.length < 2) {
    errors["slug"] = "Slug must be at least 2 characters";
  } else if (trimmedSlug.length > 50) {
    errors["slug"] = "Slug must be less than 50 characters";
  }

  return errors;
}
